import { useState, type FC } from "react";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import FilterChip from "../../atoms/inputs/FilterChip";
import { Col, Row } from "react-grid-system";
import TextField from "../../atoms/inputs/TextField";
import TextButton from "../../atoms/buttons/TextButton";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import { theme } from "../../../theme";
import { HorizontalSpacer, VerticalSpacer } from "../../atoms/Spacers";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText, {
  TableHeaderTextSorting,
} from "../../atoms/typography/TableHeaderText";
import TableRow from "../../atoms/TableRow";
import BodySmall from "../../atoms/typography/BodySmall";
import dayjs from "dayjs";
import ChipTag from "../../atoms/ChipTag";
import HandleUserActionRow from "./HandleUserActionRow";
import Card from "../../atoms/Card";
import { useUsers } from "../../../graphql/hooks/rolesAndPermissions/useUsers";
import { useUserGroups } from "../../../graphql/hooks/rolesAndPermissions/useUserGroups";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../../molecules/Alert";
import type { SortDirection } from "../../../models/SortDirection";
import PaginationComponent from "../../molecules/PaginationComponent";

type UserSortBy = "FIRST_NAME" | "LAST_LOGIN_AT";

const ITEMS_PER_PAGE = 20;

interface Props {
  onUserDeleted?: () => void;
  onUserUpdated?: () => void;
}

const UsersList: FC<Props> = ({ onUserDeleted, onUserUpdated }) => {
  const [selectedGroupsFilter, setSelectedGroupsFilter] = useState<
    string[] | undefined
  >([]);
  const [filterSpecialAccess, setFilterSpecialAccess] = useState(false);

  const { userGroups } = useUserGroups();
  const [searchUserString, setSearchUserString] = useState("");
  const [sortBy, setSortBy] = useState<UserSortBy | undefined>("FIRST_NAME");
  const [sortDirection, setSortDirection] = useState<SortDirection>("ASC");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const handleSort = (column: UserSortBy) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(column);
      setSortDirection("ASC");
    }
  };

  const clearFilters = () => {
    setSelectedGroupsFilter([]);
    setFilterSpecialAccess(false);
    setSearchUserString("");
    setCurrentPageIndex(0);
  };

  const { skynetUsers, error, loading } = useUsers(
    searchUserString || undefined,
    selectedGroupsFilter && selectedGroupsFilter.length > 0
      ? selectedGroupsFilter
      : undefined,
    filterSpecialAccess || undefined,
    sortBy,
    sortBy ? sortDirection : undefined,
    currentPageIndex * ITEMS_PER_PAGE,
    ITEMS_PER_PAGE,
  );

  return (
    <div>
      <Row nogutter justify="start" align="center">
        <TextField
          value={searchUserString}
          onChange={(value) => {
            setSearchUserString(value);
            setCurrentPageIndex(0);
          }}
          placeholder="Sök användare"
        />
        <VerticalSpacer spacing={2} />
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <MultiSelect
            options={(userGroups?.items ?? []).map((g) => ({
              value: g.id,
              label: g.name,
            }))}
            selectedOptions={selectedGroupsFilter ?? []}
            onChange={(ids) => {
              setSelectedGroupsFilter(ids);
              setCurrentPageIndex(0);
            }}
            title="Grupper"
          />
          <FilterChip
            titleKey="Anpassad behörighet"
            selected={filterSpecialAccess}
            onSelect={() => setFilterSpecialAccess(!filterSpecialAccess)}
          />
          <TextButton
            icon={<ClearIcon color={theme.colors.textSecondary} />}
            title="Rensa filter"
            onClick={clearFilters}
            color={theme.colors.textSecondary}
          />
        </div>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Card spacing={0}>
        <TableHeader>
          <Col lg={2}>
            <TableHeaderTextSorting
              onClickSorting={() => handleSort("FIRST_NAME")}
            >
              Namn
            </TableHeaderTextSorting>
          </Col>
          <Col lg={2}>
            <TableHeaderText>E-post</TableHeaderText>
          </Col>
          <Col lg={2}>
            <TableHeaderTextSorting
              onClickSorting={() => handleSort("LAST_LOGIN_AT")}
            >
              Senast inloggad
            </TableHeaderTextSorting>
          </Col>
          <Col lg={3.5}>
            <TableHeaderText>Grupper</TableHeaderText>
          </Col>
          <Col lg={2.5} />
        </TableHeader>
        {loading && !skynetUsers && <LoadingSpinner />}
        {error && (
          <Alert
            type="error"
            title="Kunde inte hämta användare"
            message={error.message}
          />
        )}
        {skynetUsers?.items?.map((user) => (
          <TableRow align="center" key={user.id}>
            <Col lg={2}>
              <BodySmall>
                {user.firstName} {user.lastName}
              </BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>{user.email}</BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(user.lastLoginAt).format("YYYY-MM-DD HH:mm")}
              </BodySmall>
            </Col>
            <Col
              lg={3.5}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {user.groups?.map((group) => (
                <ChipTag
                  key={group?.groupId}
                  tagTitleKey={
                    group?.isGroupAdmin
                      ? ` ${group?.groupName} - Gruppadmin`
                      : (group?.groupName ?? "")
                  }
                />
              ))}

              {/*<CircleActivityIndicator color={theme.colors.warning} small />*/}
            </Col>
            <Col lg={2.5}>
              <HandleUserActionRow
                userId={user.id}
                isGroupAdmin={user.groups?.some((group) => group?.isGroupAdmin)}
                onUserDeleted={onUserDeleted}
                onUserUpdated={onUserUpdated}
              />
            </Col>
          </TableRow>
        ))}

        <HorizontalSpacer spacing={1} />
        <Row nogutter justify="center">
          <PaginationComponent
            currentPageIndex={currentPageIndex}
            setCurrentPageIndex={setCurrentPageIndex}
            numberOfPages={
              Math.ceil((skynetUsers?.totalCount ?? 0) / ITEMS_PER_PAGE) || 1
            }
          />
        </Row>
        <HorizontalSpacer spacing={1} />
      </Card>
    </div>
  );
};

export default UsersList;
